import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import NewProposalDialog from './NewProposalDialog'

/**
 * The one "+ New" button, on every admin page.
 *
 * It replaces a button whose label changed with the page you were on, which
 * meant the thing you wanted to start was usually somewhere else. Each item is
 * a page's own "new" route, so nothing here creates anything by itself.
 *
 * While the menu is open, the letter beside an item picks it.
 */
export const NEW_ITEMS = [
  { key: 'J', label: 'Project',    path: '/projects?new=1' },
  // Opens the quote-or-bid chooser in place; the path is where it lands without it.
  { key: 'P', label: 'Proposal',   path: '/proposals?new=1', dialog: 'proposal' },
  { key: 'C', label: 'Client',     path: '/admin?new=1' },
  { key: 'N', label: 'Contact',    path: '/networking?add=1' },
  { key: 'I', label: 'Invoice',    path: '/invoices?new=1' },
  { key: 'A', label: 'Approval',   path: '/approvals?new=1' },
  { key: 'K', label: 'Contract',   path: '/contracts?new=1' },
  { key: 'O', label: 'Onboarding', path: '/onboarding?new=1' },
  { key: 'T', label: 'Time entry', path: '/time' },
]

export default function NewMenu() {
  const [open, setOpen] = useState(false)
  const [chooser, setChooser] = useState(false)
  const ref = useRef(null)
  const navigate = useNavigate()
  const pick = (item) => { setOpen(false); if (item.dialog === 'proposal') setChooser(true); else navigate(item.path) }

  useEffect(() => {
    if (!open) return
    function onDown(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    function onKey(e) {
      if (e.key === 'Escape') { setOpen(false); return }
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const hit = NEW_ITEMS.find(i => i.key === e.key.toUpperCase())
      if (hit) { e.preventDefault(); pick(hit) }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, navigate])

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px',
          background: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 40%, transparent)', borderRadius: 8,
          fontFamily: 'var(--fu)', fontSize: 13, fontWeight: 700, color: 'var(--accent)', cursor: 'pointer', whiteSpace: 'nowrap',
        }}>
        <svg aria-hidden="true" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1.5v9M1.5 6h9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
        New
        <svg aria-hidden="true" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2.5 4l2.5 2.5L7.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <div role="menu" aria-label="Create new" style={{
          position: 'absolute', right: 0, top: 42, zIndex: 300, width: 220, padding: 6,
          background: 'var(--card)', border: '1px solid var(--bdr)', borderRadius: 10, boxShadow: 'var(--shadow-lg)',
        }}>
          {NEW_ITEMS.map(item => (
            <button key={item.key} type="button" role="menuitem"
              onClick={() => pick(item)}
              className="sidebar-nav-item"
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 36, padding: '0 10px',
                border: 'none', borderRadius: 6, background: 'transparent', textAlign: 'left',
                fontFamily: 'var(--fu)', fontSize: 14, color: 'var(--txt)', cursor: 'pointer',
              }}>
              <span style={{ flex: 1 }}>{item.label}</span>
              <kbd style={{ fontFamily: 'var(--fu)', fontSize: 11, fontWeight: 700, color: 'var(--mut)', border: '1px solid var(--bdr2)', borderRadius: 4, padding: '0 5px' }}>{item.key}</kbd>
            </button>
          ))}
        </div>
      )}
      <NewProposalDialog open={chooser} onClose={() => setChooser(false)} />
    </div>
  )
}
